<template>
  <div class="tutoring-follow-up">
    <section class="hero">
      <div class="hero-body">
        <p class="title">{{ t.title }}</p>
        <p class="subtitle is-6">{{ t.subtitle }}</p>
      </div>
    </section>

    <!-- Resumen -->
    <section class="block-section">
      <div class="block-head">
        <h2 class="block-title">{{ t.summaryTitle }}</h2>
        <div class="block-actions">
          <b-select v-model="dias" size="is-small" @input="changeDias">
            <option v-for="d in diasOptions" :key="d" :value="d">
              {{ lastDaysLabel(d) }}
            </option>
          </b-select>
          <b-button
            size="is-small"
            icon-left="sync"
            icon-pack="fas"
            :loading="loadingSummary"
            @click="refreshAll"
          >
            {{ t.refresh }}
          </b-button>
        </div>
      </div>

      <div class="columns is-multiline">
        <div
          class="column is-3"
          v-for="card in summaryCards"
          :key="card.key"
        >
          <div
            class="stat-card"
            :class="[card.tone, card.estado ? 'is-clickable' : '']"
            @click="card.estado ? filterByEstado(card.estado) : null"
          >
            <p class="stat-label">
              <b-icon
                v-if="card.icon"
                :icon="card.icon"
                pack="fas"
                size="is-small"
              ></b-icon>
              <span>{{ card.label }}</span>
            </p>
            <p class="stat-value">{{ card.value }}</p>
            <p class="stat-hint">{{ card.hint }}</p>
          </div>
        </div>
      </div>

      <div class="situation-strip" v-if="situationChips.length > 0">
        <span class="strip-label">{{ t.bySituation }}</span>
        <b-tag
          v-for="chip in situationChips"
          :key="chip.key"
          :type="chip.type"
          class="strip-tag is-clickable"
          :class="{
            'is-selected': isSituationSelected(chip.key),
            'is-muted': chip.value === 0 && !isSituationSelected(chip.key),
          }"
          :title="situationHelp(chip.key)"
          @click.native="toggleSituation(chip.key)"
        >
          {{ chip.label }}: {{ chip.value }}
        </b-tag>
        <a
          v-if="filters.situaciones.length > 0"
          class="strip-clear"
          @click="clearSituations"
        >
          <b-icon icon="times" pack="fas" size="is-small"></b-icon>
          <span>{{ t.clearSituations }}</span>
        </a>
        <span class="strip-hint" v-else>{{ t.situationFilterHint }}</span>
      </div>
    </section>

    <div class="columns">
      <!-- Buscador, filtros y tabla -->
      <div class="column is-9">
        <div class="block-section">
          <div class="columns is-multiline">
            <div class="column is-6">
              <b-field
                label-position="on-border"
                :label="t.searchLogin"
                class="mb-0"
              >
                <b-input
                  v-model="filters.loginId"
                  :placeholder="t.loginPlaceholder"
                  icon="id-card"
                  icon-pack="fas"
                  icon-right="times"
                  icon-right-clickable
                  expanded
                  @icon-right-click="clearLoginId"
                  @keyup.native.enter="searchByLoginId"
                >
                </b-input>
                <p class="control">
                  <b-button
                    type="is-info"
                    icon-left="search"
                    icon-pack="fas"
                    :loading="loadingRows"
                    @click="searchByLoginId"
                  >
                    {{ t.search }}
                  </b-button>
                </p>
              </b-field>
            </div>
            <div class="column is-3">
              <b-field label-position="on-border" :label="t.status">
                <b-select v-model="filters.estado" expanded @input="applyFilters">
                  <option v-for="e in estadoOptions" :key="e" :value="e">
                    {{ t.states[e] }}
                  </option>
                </b-select>
              </b-field>
            </div>
            <div class="column is-3">
              <b-field label-position="on-border" :label="t.club">
                <b-select
                  v-model="filters.clubId"
                  expanded
                  :loading="loadingClubes"
                  @input="applyScopeFilters"
                >
                  <option :value="null">{{ t.allClubs }}</option>
                  <option
                    v-for="club in clubes"
                    :key="club._id"
                    :value="club._id"
                  >
                    {{ club.Nombre }}
                  </option>
                </b-select>
              </b-field>
            </div>
            <div class="column is-5">
              <b-field label-position="on-border" :label="t.agent">
                <b-autocomplete
                  v-model="filters.agente"
                  :data="filteredAgents"
                  :placeholder="t.allAgents"
                  icon="user-tie"
                  icon-pack="fas"
                  icon-right="times"
                  icon-right-clickable
                  clearable
                  open-on-focus
                  expanded
                  @icon-right-click="clearAgente"
                  @select="applyScopeFilters"
                  @keyup.native.enter="applyScopeFilters"
                >
                </b-autocomplete>
              </b-field>
            </div>
            <div class="column is-3">
              <b-field label-position="on-border" :label="t.pageSize">
                <b-select v-model="limite" expanded @input="changeLimite">
                  <option v-for="l in limiteOptions" :key="l" :value="l">
                    {{ l }}
                  </option>
                </b-select>
              </b-field>
            </div>
            <div class="column is-4 is-flex is-justify-content-flex-end">
              <b-button
                icon-left="eraser"
                icon-pack="fas"
                type="is-danger is-light"
                class="mr-2"
                @click="clearFilters"
              >
                {{ t.clear }}
              </b-button>
              <b-button
                icon-left="search"
                icon-pack="fas"
                type="is-info"
                :loading="loadingRows"
                @click="applyScopeFilters"
              >
                {{ t.search }}
              </b-button>
            </div>
          </div>

          <div class="active-filters" v-if="activeLoginId">
            <b-tag type="is-info is-light" closable @close="clearLoginId(true)">
              {{ t.showingAllFor }} {{ activeLoginId }}
            </b-tag>
          </div>

          <div class="active-filters" v-if="filters.situaciones.length > 0">
            <b-tag
              v-for="situacion in filters.situaciones"
              :key="`filtro-${situacion}`"
              :type="situationType(situacion)"
              class="strip-tag"
              closable
              @close="toggleSituation(situacion)"
            >
              {{ situationLabel(situacion) }}
            </b-tag>
          </div>

          <!-- El API rechazo los filtros: el listado no esta filtrado -->
          <b-message
            v-if="error"
            type="is-danger"
            size="is-small"
            has-icon
            icon-pack="fas"
            :closable="false"
            class="mt-3"
          >
            {{ error }}
          </b-message>

          <!-- El conteo es sobre las N reuniones mas recientes -->
          <b-message
            v-else-if="truncado && mensaje"
            type="is-warning"
            size="is-small"
            has-icon
            icon-pack="fas"
            :closable="false"
            class="mt-3"
          >
            {{ mensaje }}
          </b-message>

          <b-message
            v-else-if="mensaje"
            type="is-info"
            size="is-small"
            has-icon
            icon-pack="fas"
            :closable="false"
            class="mt-3"
          >
            {{ mensaje }}
          </b-message>
        </div>

        <!-- Dos ejes distintos: en que quedo el tutorial y cuanto plazo queda -->
        <p class="axis-legend">
          <b-icon icon="info-circle" pack="fas" size="is-small"></b-icon>
          <span>
            <b>{{ t.colSituation }}</b>: {{ t.legendSituation }}
            &nbsp;·&nbsp;
            <b>{{ t.colDeadline }}</b>: {{ t.legendDeadline }}
          </span>
        </p>

        <b-table
          :data="rows"
          :loading="loadingRows"
          detailed
          detail-key="rowKey"
          :show-detail-icon="true"
          :paginated="true"
          backend-pagination
          :total="total"
          :per-page="limite"
          :current-page.sync="pagina"
          pagination-simple
          pagination-position="bottom"
          :row-class="(row) => rowClass(row)"
          :striped="true"
          :hoverable="true"
          :narrowed="true"
          :mobile-cards="true"
          aria-next-label="Next page"
          aria-previous-label="Previous page"
          aria-page-label="Page"
          aria-current-label="Current page"
          @page-change="onPageChange"
        >
          <template #bottom-left>
            <b class="is-size-7 has-text-grey">{{ resultsLabel }}</b>
          </template>

          <b-table-column :label="t.colLoginId" v-slot="props" width="110">
            <a
              v-if="props.row.loginId"
              class="login-link"
              :title="t.searchLogin"
              @click="searchByLoginId(props.row.loginId)"
            >
              {{ props.row.loginId }}
            </a>
            <b-tooltip
              v-else
              :label="guestEmail(props.row) || t.noLoginId"
              position="is-right"
              multilined
            >
              <span class="has-text-grey is-size-7">
                {{ guestEmail(props.row) || t.noLoginId }}
              </span>
            </b-tooltip>
          </b-table-column>

          <b-table-column :label="t.colMember" v-slot="props">
            <div class="cell-stack">
              <span class="cell-main">{{ memberName(props.row) }}</span>
              <span class="cell-sub" v-if="props.row.numeroContrato">
                {{ t.contract }} {{ props.row.numeroContrato }}
              </span>
            </div>
          </b-table-column>

          <b-table-column :label="t.colClub" v-slot="props" width="130">
            <span>{{ (props.row.club && props.row.club.nombre) || t.noData }}</span>
          </b-table-column>

          <b-table-column :label="t.colMeeting" v-slot="props" width="180">
            <div class="cell-stack" v-if="props.row.reunion">
              <span class="cell-main">
                {{ meetingMoment(props.row.reunion).date }}
              </span>
              <span class="cell-sub">
                {{ meetingMoment(props.row.reunion).time }}
                <span class="tz" v-if="props.row.reunion.zonaHoraria">
                  ({{ props.row.reunion.zonaHoraria }})
                </span>
              </span>
            </div>
            <span class="has-text-grey is-italic is-size-7" v-else>
              {{ t.noMeeting }}
            </span>
          </b-table-column>

          <b-table-column :label="t.colAgent" v-slot="props" width="150">
            <span>{{ meetingAgent(props.row) || t.noData }}</span>
          </b-table-column>

          <b-table-column
            :label="t.colDuration"
            v-slot="props"
            width="100"
            centered
          >
            <span v-if="hasDuration(props.row)">
              {{ props.row.reunion.duracionMinutos }} {{ t.minutes }}
            </span>
            <b-tooltip
              v-else-if="props.row.situacion === 'sin_calcular'"
              :label="t.pendingCalc"
              position="is-left"
              multilined
            >
              <b-icon
                icon="hourglass-half"
                pack="fas"
                size="is-small"
                type="is-grey"
              ></b-icon>
            </b-tooltip>
            <span class="has-text-grey" v-else>{{ t.noData }}</span>
          </b-table-column>

          <b-table-column
            :label="t.colSituation"
            v-slot="props"
            width="150"
            centered
          >
            <b-tooltip
              :label="situationHelp(props.row.situacion)"
              position="is-left"
              multilined
            >
              <b-tag
                :type="situationType(props.row.situacion)"
                :class="props.row.situacion === 'vencida' ? 'tag-lost' : ''"
              >
                <b-icon
                  v-if="situationIcon(props.row.situacion)"
                  :icon="situationIcon(props.row.situacion)"
                  pack="fas"
                  size="is-small"
                  class="tag-icon"
                ></b-icon>
                {{ situationLabel(props.row.situacion) }}
              </b-tag>
            </b-tooltip>
          </b-table-column>

          <b-table-column
            :label="t.colDeadline"
            v-slot="props"
            width="140"
            centered
          >
            <b-tooltip
              :label="deadlineCell(props.row).help"
              position="is-left"
              multilined
            >
              <b-tag
                v-if="deadlineCell(props.row).type"
                :type="deadlineCell(props.row).type"
                :class="deadlineCell(props.row).lost ? 'tag-lost' : ''"
              >
                <b-icon
                  v-if="deadlineCell(props.row).icon"
                  :icon="deadlineCell(props.row).icon"
                  pack="fas"
                  size="is-small"
                  class="tag-icon"
                ></b-icon>
                {{ deadlineCell(props.row).label }}
              </b-tag>
              <span class="has-text-grey" v-else>
                {{ deadlineCell(props.row).label }}
              </span>
            </b-tooltip>
          </b-table-column>

          <b-table-column
            :label="t.colRecording"
            v-slot="props"
            width="130"
            centered
          >
            <b-button
              v-if="recordingLink(props.row)"
              type="is-link is-light"
              size="is-small"
              icon-left="play"
              icon-pack="fas"
              @click="openRecording(recordingLink(props.row))"
            >
              {{ t.viewRecording }}
            </b-button>
            <b-tooltip
              v-else-if="noConsent(props.row)"
              :label="t.noConsent"
              position="is-left"
              multilined
            >
              <b-icon
                icon="video-slash"
                pack="fas"
                size="is-small"
                type="is-grey"
              ></b-icon>
            </b-tooltip>
            <span class="has-text-grey" v-else>{{ t.noData }}</span>
          </b-table-column>

          <template #detail="props">
            <div class="columns detail-grid">
              <div class="column is-3">
                <p class="detail-title">{{ t.detailMeeting }}</p>
                <template v-if="props.row.reunion">
                  <p class="detail-line" v-if="props.row.reunion.titulo">
                    <b>{{ t.meetingTitle }}:</b> {{ props.row.reunion.titulo }}
                  </p>
                  <p class="detail-line" v-if="props.row.reunion.estado">
                    <b>{{ t.meetingStatus }}:</b> {{ props.row.reunion.estado }}
                  </p>
                  <p class="detail-line">
                    <b>{{ t.occurredLabel }}:</b>
                    {{ boolLabel(props.row.reunion.ocurrio) }}
                  </p>
                  <p class="detail-line">
                    <b>{{ t.attendedLabel }}:</b>
                    {{ boolLabel(props.row.reunion.asistio) }}
                  </p>
                  <p class="detail-line" v-if="props.row.reunion.zonaHoraria">
                    <b>{{ t.timezone }}:</b> {{ props.row.reunion.zonaHoraria }}
                  </p>
                  <p class="detail-line" v-if="props.row.reunion.engagementId">
                    <b>{{ t.engagement }}:</b>
                    {{ props.row.reunion.engagementId }}
                  </p>
                </template>
                <p class="detail-line has-text-grey" v-else>
                  {{ t.noMeeting }}
                </p>
              </div>

              <div class="column is-3">
                <p class="detail-title">{{ t.detailTutoring }}</p>
                <p class="detail-line">
                  <b-tag
                    :type="
                      props.row.tutoria && props.row.tutoria.registrada
                        ? 'is-success is-light'
                        : 'is-warning is-light'
                    "
                  >
                    {{
                      props.row.tutoria && props.row.tutoria.registrada
                        ? t.registered
                        : t.notRegistered
                    }}
                  </b-tag>
                </p>
                <template v-if="props.row.tutoria">
                  <p class="detail-line" v-if="props.row.tutoria.resultado">
                    <b>{{ t.result }}:</b> {{ props.row.tutoria.resultado }}
                  </p>
                  <p class="detail-line" v-if="props.row.tutoria.fechaSesion">
                    <b>{{ t.sessionDate }}:</b>
                    {{ formatDate(props.row.tutoria.fechaSesion) }}
                  </p>
                  <p class="detail-line" v-if="props.row.tutoria.creadoPor">
                    <b>{{ t.createdBy }}:</b> {{ props.row.tutoria.creadoPor }}
                  </p>
                </template>
              </div>

              <div class="column is-3">
                <p class="detail-title">{{ t.detailDeal }}</p>
                <p class="detail-line">
                  <b-tag
                    :type="
                      props.row.deal && props.row.deal.asociado
                        ? 'is-success is-light'
                        : 'is-warning is-light'
                    "
                  >
                    {{
                      props.row.deal && props.row.deal.asociado
                        ? t.associated
                        : t.notAssociated
                    }}
                  </b-tag>
                </p>
                <p class="detail-line" v-if="props.row.deal && props.row.deal.id">
                  <b>ID:</b> {{ props.row.deal.id }}
                </p>

                <p class="detail-title mt-4">{{ t.detailRecording }}</p>
                <p class="detail-line" v-if="props.row.grabacion">
                  <b>{{ t.consent }}:</b>
                  {{ boolLabel(props.row.grabacion.consentimiento) }}
                </p>
                <p
                  class="detail-line"
                  v-if="props.row.grabacion && props.row.grabacion.estado"
                >
                  <b>{{ t.recordingState }}:</b>
                  {{ props.row.grabacion.estado }}
                </p>
                <p class="detail-line" v-if="recordingLink(props.row)">
                  <a
                    :href="recordingLink(props.row)"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ t.viewRecording }}
                  </a>
                </p>
              </div>

              <div class="column is-3">
                <p class="detail-title">{{ t.detailMissing }}</p>
                <template
                  v-if="props.row.faltantes && props.row.faltantes.length > 0"
                >
                  <b-tag
                    v-for="(falta, index) in props.row.faltantes"
                    :key="index"
                    type="is-danger is-light"
                    class="strip-tag"
                  >
                    {{ falta }}
                  </b-tag>
                </template>
                <p class="detail-line has-text-grey" v-else>
                  {{ t.nothingMissing }}
                </p>
              </div>
            </div>
          </template>

          <template #empty>
            <div class="has-text-centered has-text-grey empty-table">
              <b-icon icon="calendar-times" pack="fas" size="is-medium"></b-icon>
              <p>{{ t.empty }}</p>
            </div>
          </template>
        </b-table>
      </div>

      <!-- Panel de pendientes por agente -->
      <div class="column is-3">
        <div class="side-panel">
          <p class="panel-title">{{ t.pendingByAgent }}</p>
          <p class="panel-sub">{{ lastDaysLabel(dias) }}</p>
          <b-skeleton v-if="loadingSummary" :count="5" height="28"></b-skeleton>
          <template v-else>
            <ul class="agent-list" v-if="pendientesPorAgente.length > 0">
              <li
                v-for="(agente, index) in pendientesPorAgente"
                :key="`${agente.agente}-${index}`"
                :class="{ 'is-active': filters.agente === agente.agente }"
                @click="filterByAgente(agente.agente)"
              >
                <span class="agent-name">{{ agente.agente || t.noData }}</span>
                <b-tag :type="agente.pendientes > 0 ? 'is-warning' : 'is-light'">
                  {{ agente.pendientes }}
                </b-tag>
              </li>
            </ul>
            <p class="empty-panel" v-else>{{ t.noPendingAgents }}</p>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import "moment-timezone";
import i18n from "../../utils/i18n";

const API_URL = process.env.VUE_APP_API_URL;

const ESTADOS = [
  "todos",
  "completos",
  "incompletos",
  "sin_tutoria",
  "vencidos",
  "por_vencer",
];

const SITUACIONES = [
  "agendada",
  "no_show",
  "sin_calcular",
  "falta_registrar",
  "vencida",
  "falta_deal",
  "completa",
  "sin_reunion",
];

/**
 * Jerarquia de color: lo ambar es lo accionable y pesa mas que lo perdido.
 * `vencida` va en rojo apagado porque ya no se puede hacer nada con ella.
 */
const SITUACION_TYPES = {
  agendada: "is-info is-light",
  no_show: "is-dark",
  sin_calcular: "is-light",
  falta_registrar: "is-warning",
  vencida: "is-danger is-light",
  falta_deal: "is-warning is-light",
  completa: "is-success",
  sin_reunion: "is-light",
};

const SITUACION_ICONS = {
  agendada: "calendar",
  no_show: "user-slash",
  sin_calcular: "hourglass-half",
  falta_registrar: "pen",
  vencida: "lock",
  falta_deal: "unlink",
  completa: "check",
  sin_reunion: "calendar-times",
};

/**
 * Umbral por defecto para el resaltado ámbar mientras el API no responde.
 * El valor bueno viene en `umbralPorVencer` del listado y del resumen.
 */
const UMBRAL_POR_VENCER_DEFAULT = 2;

const defaultFilters = () => ({
  loginId: null,
  estado: "todos",
  clubId: null,
  agente: null,
  situaciones: [],
});

export default {
  name: "TutoringFollowUp",

  data() {
    return {
      i18n,
      iso: "es",

      // Resumen
      dias: 30,
      diasOptions: [7, 30, 90],
      resumen: {},
      loadingSummary: false,

      // Seguimiento
      rows: [],
      total: 0,
      paginas: 1,
      pagina: 1,
      limite: 50,
      limiteOptions: [25, 50, 100, 200, 500],
      loadingRows: false,
      mensaje: null,
      truncado: false,
      error: null,
      umbralPorVencer: UMBRAL_POR_VENCER_DEFAULT,

      // Filtros
      filters: defaultFilters(),
      activeLoginId: null,
      estadoOptions: ESTADOS,
      clubes: [],
      loadingClubes: false,
    };
  },

  computed: {
    t() {
      return (this.i18n.tutoringFollowUp || {})[this.iso] || {};
    },

    pendientesPorAgente() {
      const pendientes = this.resumen.pendientesPorAgente || [];
      return pendientes
        .slice()
        .sort((a, b) => (b.pendientes || 0) - (a.pendientes || 0));
    },

    agents() {
      const names = new Set();
      this.pendientesPorAgente.forEach((a) => {
        if (a.agente) names.add(a.agente);
      });
      this.rows.forEach((row) => {
        const agente = this.meetingAgent(row);
        if (agente) names.add(agente);
      });
      return Array.from(names).sort((a, b) => a.localeCompare(b));
    },

    filteredAgents() {
      const search = (this.filters.agente || "").toLowerCase().trim();
      if (!search) return this.agents;
      return this.agents.filter((a) => a.toLowerCase().includes(search));
    },

    summaryCards() {
      const r = this.resumen || {};
      return [
        {
          key: "reuniones",
          label: this.t.meetings,
          value: this.num(r.reuniones),
          hint: `${this.num(r.porOcurrir)} ${this.t.toOccur} · ${this.num(
            r.ocurridas,
          )} ${this.t.occurred}`,
          icon: "calendar-check",
          tone: "is-neutral",
        },
        {
          key: "asistieron",
          label: this.t.attended,
          value: this.num(r.asistieron),
          hint: `${this.num(r.noShow)} ${this.t.noShow}`,
          icon: "user-check",
          tone: "is-neutral",
        },
        {
          key: "completos",
          label: this.t.complete,
          value: this.num(r.completos),
          hint: this.t.completeHint,
          icon: "check-double",
          tone: "is-ok",
          estado: "completos",
        },
        {
          key: "sinTutoria",
          label: this.t.withoutTutoring,
          value: this.num(r.sinTutoria),
          hint: this.t.withoutTutoringHint,
          icon: "clipboard-list",
          tone: "is-neutral",
          estado: "sin_tutoria",
        },
        {
          key: "sinTutoriaPorVencer",
          label: this.t.aboutToExpire,
          value: this.num(r.sinTutoriaPorVencer),
          hint: this.t.aboutToExpireHint,
          icon: "exclamation-triangle",
          tone: "is-alert",
          estado: "por_vencer",
        },
        {
          key: "sinTutoriaVencidos",
          label: this.t.expired,
          value: this.num(r.sinTutoriaVencidos),
          hint: this.t.expiredHint,
          icon: "lock",
          tone: "is-lost",
          estado: "vencidos",
        },
        {
          key: "sinDeal",
          label: this.t.withoutDeal,
          value: this.num(r.sinDeal),
          hint: this.t.withoutDealHint,
          icon: "handshake",
          tone: "is-neutral",
        },
        {
          key: "conGrabacion",
          label: this.t.withRecording,
          value: this.num(r.conGrabacion),
          hint: this.t.withRecordingHint,
          icon: "video",
          tone: "is-neutral",
        },
      ];
    },

    situationChips() {
      const porSituacion = this.resumen.porSituacion || {};
      const keys = Object.keys(porSituacion);
      // El resumen ya trae las ocho llaves con cero donde no hay nada; el
      // orden del flujo lo pone el front y cualquier llave extra va al final.
      const ordered = SITUACIONES.concat(
        keys.filter((k) => !SITUACIONES.includes(k)),
      );
      return ordered.map((key) => ({
        key,
        label: this.situationLabel(key),
        value: this.num(porSituacion[key]),
        type: SITUACION_TYPES[key] || "is-light",
      }));
    },

    resultsLabel() {
      const results = (this.t.results || "").replace("{n}", this.total);
      if (this.paginas <= 1) return results;
      const pageOf = (this.t.pageOf || "")
        .replace("{p}", this.pagina)
        .replace("{t}", this.paginas);
      return `${results} · ${pageOf}`;
    },
  },

  async created() {
    this.iso = localStorage.getItem("iso") || "es";
    this.$bus.on("change-iso", () => {
      this.iso = localStorage.getItem("iso") || "es";
    });

    const limiteLocalStorage = Number(
      localStorage.getItem("limiteTutoringFollowUp"),
    );
    if (this.limiteOptions.includes(limiteLocalStorage))
      this.limite = limiteLocalStorage;

    await this.listAllClubes();
    this.listSummary();
    this.listRows();
  },

  methods: {
    /**
     * Consultas
     */
    async listSummary() {
      try {
        this.loadingSummary = true;
        // El resumen lleva los mismos club/agente que el listado para que las
        // tarjetas y la tabla hablen del mismo universo.
        const params = { dias: this.dias };
        if (this.filters.clubId) params.clubId = this.filters.clubId;
        if (this.filters.agente) params.agente = this.filters.agente.trim();

        const { data } = await axios.get(
          `${API_URL}/tutorias/seguimiento/resumen`,
          { params },
        );
        this.resumen = data?.resumen || data || {};
        if (typeof this.resumen.umbralPorVencer === "number")
          this.umbralPorVencer = this.resumen.umbralPorVencer;
      } catch (err) {
        console.log(err);
        this.$buefy.toast.open({
          message: this.t.errorSummary,
          type: "is-danger",
        });
      } finally {
        this.loadingSummary = false;
      }
    },

    async listRows() {
      try {
        this.loadingRows = true;
        this.error = null;
        const params = {
          limite: this.limite,
          pagina: this.pagina,
        };
        if (this.filters.loginId) params.loginId = this.filters.loginId.trim();
        if (this.filters.estado && this.filters.estado !== "todos")
          params.estado = this.filters.estado;
        if (this.filters.clubId) params.clubId = this.filters.clubId;
        if (this.filters.agente) params.agente = this.filters.agente.trim();
        if (this.filters.situaciones.length > 0)
          params.situacion = this.filters.situaciones.join(",");
        // Sobre el mismo periodo que el resumen, para que los numeros cuadren.
        // Al buscar un LoginID se quiere el historial completo del socio.
        if (!params.loginId) params.dias = this.dias;

        const { data } = await axios.get(`${API_URL}/tutorias/seguimiento`, {
          params,
        });

        if (data && data.success === false) {
          // El filtro no se aplico: no mostramos el listado como si lo fuera
          this.setRowsError(data.mensaje);
          return;
        }

        this.rows = this.sortByMeetingDesc(data?.filas || []);
        this.total = data?.total || 0;
        this.paginas = data?.paginas || this.calcPaginas(this.total);
        this.mensaje = data?.mensaje || null;
        this.truncado = data?.truncado === true;
        this.activeLoginId = params.loginId || null;
        if (typeof data?.umbralPorVencer === "number")
          this.umbralPorVencer = data.umbralPorVencer;
      } catch (err) {
        console.log(err);
        // 400: situacion o filtro fuera del catalogo, el API lo rechazo
        this.setRowsError(
          err?.response?.data?.mensaje || err?.response?.data?.message,
        );
      } finally {
        this.loadingRows = false;
      }
    },

    setRowsError(mensaje) {
      this.rows = [];
      this.total = 0;
      this.paginas = 1;
      this.mensaje = null;
      this.truncado = false;
      this.error = mensaje || this.t.errorRows;
      this.$buefy.toast.open({
        message: this.error,
        type: "is-danger",
        duration: 5000,
      });
    },

    calcPaginas(total) {
      return Math.max(1, Math.ceil((total || 0) / this.limite));
    },

    async listAllClubes() {
      try {
        this.loadingClubes = true;
        const { data } = await axios.post(`${API_URL}/club/list`, {
          select: "Nombre",
        });
        this.clubes = data?.clubes || [];
      } catch (err) {
        console.log(err);
        this.$buefy.toast.open({
          message: this.t.errorClubs,
          type: "is-danger",
        });
      } finally {
        this.loadingClubes = false;
      }
    },

    refreshAll() {
      this.listSummary();
      this.listRows();
    },

    /**
     * Filtros
     */
    applyFilters() {
      this.pagina = 1;
      this.listRows();
    },

    /**
     * Filtros que tambien acota el resumen: club, agente y periodo.
     */
    applyScopeFilters() {
      this.pagina = 1;
      this.listRows();
      this.listSummary();
    },

    changeDias() {
      this.applyScopeFilters();
    },

    searchByLoginId(loginId = null) {
      // Buscar un LoginID muestra todas las reuniones de ese socio
      if (typeof loginId === "string" || typeof loginId === "number")
        this.filters.loginId = `${loginId}`;

      if (this.filters.loginId) {
        this.filters.estado = "todos";
        this.filters.clubId = null;
        this.filters.agente = null;
        this.filters.situaciones = [];
      }
      // Limpia club y agente: el resumen vuelve a su universo completo
      this.applyScopeFilters();
    },

    filterByEstado(estado) {
      this.filters.loginId = null;
      this.filters.estado = estado;
      this.filters.situaciones = [];
      this.applyFilters();
    },

    isSituationSelected(situacion) {
      return this.filters.situaciones.includes(situacion);
    },

    toggleSituation(situacion) {
      const index = this.filters.situaciones.indexOf(situacion);
      if (index >= 0) this.filters.situaciones.splice(index, 1);
      else this.filters.situaciones.push(situacion);
      this.applyFilters();
    },

    clearSituations() {
      this.filters.situaciones = [];
      this.applyFilters();
    },

    filterByAgente(agente) {
      if (!agente) return;
      this.filters.loginId = null;
      this.filters.agente = this.filters.agente === agente ? null : agente;
      this.applyScopeFilters();
    },

    clearLoginId(search = false) {
      this.filters.loginId = null;
      if (search === true) this.applyFilters();
    },

    clearAgente() {
      this.filters.agente = null;
      this.applyScopeFilters();
    },

    clearFilters() {
      this.filters = defaultFilters();
      this.applyScopeFilters();
    },

    changeLimite() {
      localStorage.setItem("limiteTutoringFollowUp", this.limite);
      this.applyFilters();
    },

    onPageChange(page) {
      this.pagina = page;
      this.listRows();
    },

    /**
     * Formatos
     */
    num(value) {
      return typeof value === "number" ? value : value || 0;
    },

    sortByMeetingDesc(filas) {
      return filas
        .map((fila, index) => ({
          ...fila,
          rowKey: `${fila.contratoId || fila.loginId || "row"}-${
            (fila.reunion && fila.reunion.engagementId) || index
          }`,
        }))
        .sort((a, b) => {
          const dateA = a.reunion?.inicio
            ? moment.utc(a.reunion.inicio).valueOf()
            : null;
          const dateB = b.reunion?.inicio
            ? moment.utc(b.reunion.inicio).valueOf()
            : null;
          // Las filas sin reunión agendada van al final
          if (dateA === null && dateB === null) return 0;
          if (dateA === null) return 1;
          if (dateB === null) return -1;
          return dateB - dateA;
        });
    },

    meetingMoment(reunion) {
      if (!reunion || !reunion.inicio) return { date: this.t.noData, time: "" };
      let date = moment.utc(reunion.inicio);
      if (reunion.zonaHoraria && moment.tz.zone(reunion.zonaHoraria))
        date = date.tz(reunion.zonaHoraria);
      else date = date.local();
      return {
        date: date.format("DD/MM/YYYY"),
        time: date.format("hh:mm A"),
      };
    },

    formatDate(date) {
      if (!date) return this.t.noData;
      return moment.utc(date).local().format("DD/MM/YYYY hh:mm A");
    },

    memberName(row) {
      const socio = row.socio || {};
      return socio.nombre || socio.email || this.t.guest;
    },

    guestEmail(row) {
      const socio = row.socio || {};
      return socio.email || null;
    },

    meetingAgent(row) {
      return (row.reunion && row.reunion.agente) || null;
    },

    hasDuration(row) {
      return (
        row.reunion && typeof row.reunion.duracionMinutos === "number" &&
        row.reunion.duracionMinutos > 0
      );
    },

    hasDaysLeft(row) {
      return row.plazo && typeof row.plazo.diasRestantes === "number";
    },

    isRegistered(row) {
      return !!(row.tutoria && row.tutoria.registrada);
    },

    /**
     * La columna Plazo habla solo del tiempo para registrar la Tutoría, nunca
     * del resultado del tutorial — eso lo dice la columna Situación. Cuando la
     * Tutoría ya se registró el plazo deja de aplicar, aunque haya vencido.
     */
    deadlineCell(row) {
      const plazo = row.plazo || {};
      const dias = plazo.diasRestantes;

      if (this.isRegistered(row)) {
        return plazo.vencido
          ? {
              label: this.t.registeredLate,
              type: "is-light",
              icon: "history",
              help: this.t.deadlineHelpLate,
            }
          : { label: this.t.noData, type: null, help: this.t.deadlineHelpDone };
      }

      if (plazo.vencido || row.situacion === "vencida") {
        return {
          label: this.t.outOfTime,
          type: "is-danger is-light",
          icon: "lock",
          help: this.t.deadlineHelpLost,
          lost: true,
        };
      }

      if (typeof dias === "number") {
        // Ambar = "hay que ir a buscarlo", una sola cosa en toda la pantalla:
        // mismo criterio que el resaltado de la fila y que la tarjeta.
        const urgente =
          row.situacion === "falta_registrar" && dias <= this.umbralPorVencer;
        return {
          label: `${dias} ${this.t.daysShort}`,
          type: urgente ? "is-warning" : "is-light",
          icon: urgente ? "clock" : null,
          help: (this.t.deadlineHelpDays || "").replace("{n}", dias),
        };
      }

      return { label: this.t.noData, type: null, help: this.t.deadlineHelpNone };
    },

    recordingLink(row) {
      return (row.grabacion && row.grabacion.link) || null;
    },

    noConsent(row) {
      return !!row.grabacion && row.grabacion.consentimiento === false;
    },

    openRecording(link) {
      if (link) window.open(link, "_blank", "noopener");
    },

    boolLabel(value) {
      if (value === null || value === undefined) return this.t.noData;
      return value ? this.t.yes : this.t.no;
    },

    situationLabel(situacion) {
      return (this.t.situations || {})[situacion] || situacion || this.t.noData;
    },

    situationHelp(situacion) {
      return (this.t.situationsHelp || {})[situacion] || "";
    },

    situationType(situacion) {
      return SITUACION_TYPES[situacion] || "is-light";
    },

    situationIcon(situacion) {
      return SITUACION_ICONS[situacion] || null;
    },

    lastDaysLabel(dias) {
      return (this.t.lastDays || "{n}").replace("{n}", dias);
    },

    /**
     * Resaltado de filas. Solo se resalta lo que habla del registro de la
     * Tutoría, no cualquier plazo cumplido: una fila con la Tutoría ya
     * registrada no se pinta aunque el plazo haya pasado.
     *
     * Ámbar: urge registrar, todavía se puede (el umbral lo define el API).
     * Rojo apagado: ya se perdió, no hay nada que hacer.
     */
    rowClass(row) {
      if (this.isRegistered(row)) return "";
      if (row.situacion === "vencida" || (row.plazo && row.plazo.vencido))
        return "row-lost";
      if (
        row.situacion === "falta_registrar" &&
        this.hasDaysLeft(row) &&
        row.plazo.diasRestantes <= this.umbralPorVencer
      )
        return "row-urgent";
      return "";
    },
  },
};
</script>

<style scoped>
.block-section {
  background-color: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  padding: 1.25rem;
  margin-bottom: 1.5rem;
}

.block-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.block-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #363636;
}

.block-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Tarjetas del resumen */
.stat-card {
  height: 100%;
  border: 1px solid #e8e8e8;
  border-left: 4px solid #b5b5b5;
  border-radius: 0.5rem;
  padding: 0.9rem 1rem;
  background-color: #fafafa;
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}

.stat-card.is-clickable {
  cursor: pointer;
}

.stat-card.is-clickable:hover {
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.stat-card.is-ok {
  border-left-color: #48c774;
}

/* Lo accionable pesa mas que lo perdido: el ambar va marcado, el rojo apagado */
.stat-card.is-alert {
  border-left-color: #ffa726;
  border-left-width: 6px;
  background-color: #fff8e6;
}

.stat-card.is-lost {
  border-left-color: #cc7a8a;
  background-color: #f7eef0;
}

.stat-label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #7a7a7a;
  font-weight: 600;
}

.stat-value {
  font-size: 1.9rem;
  font-weight: 700;
  line-height: 1.2;
  color: #363636;
}

.stat-card.is-alert .stat-value {
  color: #c87a00;
}

.stat-card.is-lost .stat-value {
  color: #9b4a5b;
}

/* Pastilla de algo ya perdido: legible, pero sin gritar */
.tag-lost {
  text-decoration: line-through;
  text-decoration-thickness: 1px;
  opacity: 0.9;
}

.tag-icon {
  margin-right: 0.2rem;
}

.axis-legend {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  color: #7a7a7a;
  margin-bottom: 0.6rem;
}

.stat-hint {
  font-size: 0.75rem;
  color: #7a7a7a;
}

.situation-strip {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.5rem;
  padding-top: 0.75rem;
  border-top: 1px solid #f0f0f0;
}

.strip-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #7a7a7a;
  margin-right: 0.25rem;
}

.strip-tag {
  margin-right: 0.25rem;
  margin-bottom: 0.25rem;
}

.strip-tag.is-clickable {
  cursor: pointer;
  border: 1px solid transparent;
}

.strip-tag.is-clickable:hover {
  filter: brightness(0.95);
}

.strip-tag.is-selected {
  border-color: #363636;
  box-shadow: 0 0 0 1px #363636;
  font-weight: 700;
}

.strip-tag.is-muted {
  opacity: 0.45;
}

.strip-clear {
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  font-size: 0.75rem;
  color: #f14668;
  cursor: pointer;
  margin-left: 0.25rem;
}

.strip-hint {
  font-size: 0.72rem;
  color: #b5b5b5;
  margin-left: 0.25rem;
}

.active-filters {
  margin-top: 0.75rem;
}

/* Celdas */
.cell-stack {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}

.cell-main {
  font-weight: 600;
  color: #363636;
}

.cell-sub {
  font-size: 0.75rem;
  color: #7a7a7a;
}

.tz {
  font-size: 0.7rem;
}

.login-link {
  font-weight: 700;
  color: #3e8ed0;
  cursor: pointer;
}

.empty-table {
  padding: 2.5rem 1rem;
}

/* Detalle */
.detail-grid {
  font-size: 0.85rem;
}

.detail-title {
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.72rem;
  letter-spacing: 0.03em;
  color: #7a7a7a;
  margin-bottom: 0.35rem;
}

.detail-line {
  margin-bottom: 0.2rem;
  word-break: break-word;
}

/* Panel lateral */
.side-panel {
  position: sticky;
  top: 1rem;
  background-color: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  padding: 1rem;
}

.panel-title {
  font-weight: 700;
  color: #363636;
}

.panel-sub {
  font-size: 0.75rem;
  color: #7a7a7a;
  margin-bottom: 0.75rem;
}

.agent-list {
  max-height: 60vh;
  overflow-y: auto;
}

.agent-list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.45rem 0.5rem;
  border-radius: 0.35rem;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f5;
}

.agent-list li:hover {
  background-color: #f5f9ff;
}

.agent-list li.is-active {
  background-color: #eef6ff;
  box-shadow: inset 3px 0 0 #3e8ed0;
}

.agent-name {
  font-size: 0.85rem;
  color: #363636;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.empty-panel {
  font-size: 0.85rem;
  color: #7a7a7a;
  padding: 0.5rem 0;
}
</style>

<style>
/* La tabla es ancha: que scrollee dentro de su columna y no de la pagina */
.tutoring-follow-up .table-wrapper {
  overflow-x: auto;
}

/* Resaltado de filas: ámbar urge, rojo ya se perdió */
.tutoring-follow-up tr.row-urgent td {
  background-color: #fff5df !important;
}

.tutoring-follow-up tr.row-urgent td:first-child {
  box-shadow: inset 4px 0 0 #ffa726;
}

.tutoring-follow-up tr.row-lost td {
  background-color: #f9f0f2 !important;
  color: #8a6570;
}

.tutoring-follow-up tr.row-lost td:first-child {
  box-shadow: inset 4px 0 0 #cc7a8a;
}
</style>
